/**
 * 掲示板 投稿作成・編集画面 (v3.5 Board / Phase 1)。
 * 仕様書: docs/02_work/done/plan_board_feature.md §1-6
 *
 * URL: /board/new (新規) / /board/:postId/edit (編集)
 *
 * - postId は新規作成時にクライアントで事前採番し upsertBoardPost に渡す
 *   (Phase 3 の本文メディアのアップロード先 board/imgs/{postId}/ を確定させる契約)
 * - Phase 2: 宛先ピッカー (未選択=全員宛。audience はサーバ側導出、
 *   編集時の recipientStates 差分マージは plan §2-2-3 のとおりサーバ側で行われる)
 * - AI 件名アシスト / 画像・動画挿入は Phase 3〜4
 */
import type { BoardPostType, BoardUrgency } from '@goenhub/shared';
import {
  BOARD_POST_TYPE_LABEL,
  BOARD_URGENCY_LABEL,
  isBoardPostPendingPublish,
} from '@goenhub/shared';
import { CalendarClock, FileQuestion, LayoutTemplate, Lock, Save, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { BOARD_URGENCY_BAR_COLOR } from './boardMeta';
import { AiComposeDialog } from './components/AiComposeDialog';
import { BoardReferenceAttachments } from './components/BoardReferenceAttachments';
import { BoardRichTextEditor } from './components/BoardRichTextEditor';
import { RecipientPicker } from './components/RecipientPicker';
import { SubjectAssistDialog } from './components/SubjectAssistDialog';
import { TemplatePickerDialog, type AppliedTemplate } from './components/TemplatePickerDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useRegisterBoardAttachment } from '@/hooks/useBoardAttachments';
import { useBoardCategories, useCreateBoardCategory } from '@/hooks/useBoardCategories';
import {
  newBoardPostId,
  useAssistBoardSubject,
  useBoardPost,
  useUpsertBoardPost,
} from '@/hooks/useBoardPosts';
import { uploadBoardAttachment } from '@/lib/boardMedia';
import { toDate } from '@/lib/firestoreTime';
import { paths } from '@/routes/paths';

/** bodyHtml をプレーンテキスト化する (AI 件名アシストへ渡す本文)。 */
/** AI 生成のプレーンテキストを本文 HTML に入れる際のエスケープ。 */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function htmlToPlainText(html: string): string {
  if (typeof document === 'undefined') {
    // SSR フォールバック: タグを雑に除去
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** Radix Select は空文字 value を扱えないためのセンチネル。 */
const CATEGORY_NONE = '__none__';
const CATEGORY_ADD_NEW = '__add_new__';

/** 投稿予約の最短リード時間（分）。サーバ側の MIN_PUBLISH_LEAD_MS と揃えている。 */
const MIN_PUBLISH_LEAD_MINUTES = 1;

/** Date → `<input type="datetime-local">` の値（既存の予約配信 UI と同じローカル時刻運用）。 */
function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local の値を「8月5日(火) 10:00」形式で表示する。 */
function formatPublishAt(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ja-JP', {
    month: 'long',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function BoardPostEditorPage() {
  const { postId: routePostId } = useParams<{ postId: string }>();
  const isEdit = Boolean(routePostId);
  const navigate = useNavigate();
  const { user } = useAuth();
  const categories = useBoardCategories();
  const createCategory = useCreateBoardCategory();
  const upsert = useUpsertBoardPost();
  const assistSubject = useAssistBoardSubject();
  const existing = useBoardPost(isEdit ? routePostId : null);

  // 新規作成時はクライアントで postId を事前採番 (plan §1-6)
  const [newPostId] = useState(() => newBoardPostId());
  const targetPostId = routePostId ?? newPostId;

  const [type, setType] = useState<BoardPostType>('info');
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [urgency, setUrgency] = useState<BoardUrgency>('medium');
  const [noDueDate, setNoDueDate] = useState(true);
  const [dueDate, setDueDate] = useState('');
  const [recipientUids, setRecipientUids] = useState<string[]>([]);
  // 閲覧制限 (2026-09-07): ON = 宛先と投稿者本人だけが一覧・詳細で閲覧できる
  const [restricted, setRestricted] = useState(false);
  const [bodyHtml, setBodyHtml] = useState('');
  const [initialized, setInitialized] = useState(false);

  // 投稿予約 (公開日時)。publishNow=true なら即時公開 (従来の挙動)。
  const [publishNow, setPublishNow] = useState(true);
  const [publishAt, setPublishAt] = useState('');

  /**
   * 予約を設定できるか。新規は常に可、編集は「まだ公開されていない投稿」のみ。
   * 一度公開した投稿を未公開に戻すと、既に通知が飛び宛先が閲覧・対応した状態と
   * 矛盾するため、サーバ側でも同じ条件で弾いている。
   */
  const canSchedule = !isEdit || (!!existing.data && isBoardPostPendingPublish(existing.data));

  /** datetime-local の下限 (現在時刻 + 最短リード時間)。 */
  const minPublishAt = toDatetimeLocal(new Date(Date.now() + MIN_PUBLISH_LEAD_MINUTES * 60 * 1000));

  /** 保存で予約になるか (ボタン文言・完了トーストの出し分けに使う)。 */
  const willSchedule = canSchedule && !publishNow && !!publishAt;

  // カテゴリー新規追加ダイアログ
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  // AI 件名アシスト
  const [assistOpen, setAssistOpen] = useState(false);
  const [assistSuggestions, setAssistSuggestions] = useState<string[]>([]);
  const [assistHasTitle, setAssistHasTitle] = useState(false);

  // テンプレート選択
  const [templateOpen, setTemplateOpen] = useState(false);

  // AI 作文
  const [composeOpen, setComposeOpen] = useState(false);

  // 参考ファイル: 新規投稿では保存が済むまでアップロードできないため保持する
  const [pendingRefFiles, setPendingRefFiles] = useState<File[]>([]);
  const [savingRefs, setSavingRefs] = useState(false);
  const registerRef = useRegisterBoardAttachment(targetPostId);

  const handleApplyTemplate = (t: AppliedTemplate) => {
    // 既に入力がある場合は上書き確認
    const hasContent = title.trim() !== '' || htmlToPlainText(bodyHtml) !== '';
    if (hasContent && !window.confirm('現在の件名・本文をテンプレートで置き換えますか？')) {
      return;
    }
    setTitle(t.subject);
    setBodyHtml(t.bodyHtml);
    if (t.type) setType(t.type);
    if (t.urgency) setUrgency(t.urgency);
    toast.success('テンプレートを適用しました');
  };

  // 編集時: 既存投稿の読込 → フォームへ反映 (一度だけ)
  useEffect(() => {
    if (!isEdit || initialized) return;
    const p = existing.data;
    if (!p) return;
    setType(p.type);
    setTitle(p.title);
    setCategoryId(p.categoryId);
    setUrgency(p.urgency);
    setNoDueDate(p.dueDate == null);
    setDueDate(p.dueDate ?? '');
    setRecipientUids(p.recipientUids ?? []);
    setRestricted(p.restricted === true);
    setBodyHtml(p.bodyHtml);
    // 予約中の投稿のみ公開日時を復元する（公開済みは予約に戻せない）
    const pendingAt = isBoardPostPendingPublish(p) ? toDate(p.publishAt) : null;
    setPublishNow(pendingAt === null);
    setPublishAt(pendingAt ? toDatetimeLocal(pendingAt) : '');
    setInitialized(true);
  }, [isEdit, initialized, existing.data]);

  // 編集は投稿者本人のみ (要件 §9)。本人以外は詳細へ戻す。
  useEffect(() => {
    if (!isEdit || !routePostId) return;
    if (!existing.data || !user) return;
    if (existing.data.authorUid !== user.uid) {
      toast.error('この投稿を編集できるのは投稿者本人のみです');
      navigate(paths.boardPostDetail.replace(':postId', routePostId), { replace: true });
    }
  }, [isEdit, routePostId, existing.data, user, navigate]);

  const handleCategoryChange = (value: string) => {
    if (value === CATEGORY_ADD_NEW) {
      setNewCatName('');
      setCatDialogOpen(true);
      return; // 選択は変更しない (ダイアログ確定時に反映)
    }
    setCategoryId(value === CATEGORY_NONE ? null : value);
  };

  const handleCreateCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    try {
      const { id } = await createCategory.mutateAsync({ name });
      setCategoryId(id);
      setCatDialogOpen(false);
      toast.success(`カテゴリー「${name}」を追加しました`);
    } catch (err) {
      toast.error(`追加失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleAssistSubject = async () => {
    const bodyText = htmlToPlainText(bodyHtml);
    const trimmedTitle = title.trim();
    if (!trimmedTitle && !bodyText) {
      toast.error('件名または本文を入力してから AI アシストを使用してください');
      return;
    }
    setAssistHasTitle(!!trimmedTitle);
    setAssistSuggestions([]);
    setAssistOpen(true);
    try {
      const suggestions = await assistSubject.mutateAsync({
        title: trimmedTitle || undefined,
        bodyText,
      });
      setAssistSuggestions(suggestions);
    } catch (err) {
      setAssistOpen(false);
      toast.error(`件名の生成に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('件名を入力してください');
      return;
    }
    // 予約する場合は日時が必須。未入力のまま保存して黙って即時公開になるのを防ぐ。
    if (canSchedule && !publishNow && !publishAt) {
      toast.error('公開日時を入力するか「今すぐ公開」を選んでください');
      return;
    }
    try {
      await upsert.mutateAsync({
        postId: targetPostId,
        type,
        title: title.trim(),
        bodyHtml,
        categoryId,
        urgency,
        dueDate: noDueDate ? null : dueDate || null,
        recipientUids,
        // 宛先が全員のときは OFF として保存する (サーバ側でも同じ扱い)
        restricted: restricted && recipientUids.length > 0,
        // 公開済みの投稿では publishAt をサーバ側で無視するが、
        // 予約に戻す意図が無いことを明示するため常に送る。
        publishAt:
          !canSchedule || publishNow || !publishAt ? null : new Date(publishAt).toISOString(),
      });
      // 保存が成功してから参考ファイルをアップロード + 登録する。
      // （registerBoardAttachment は投稿の存在を要求するため保存前には登録できない）
      if (pendingRefFiles.length > 0 && user?.uid) {
        setSavingRefs(true);
        let failed = 0;
        for (const f of pendingRefFiles) {
          try {
            const storagePath = await uploadBoardAttachment(targetPostId, user.uid, f);
            await registerRef.mutateAsync({
              storagePath,
              fileName: f.name,
              size: f.size,
              contentType: f.type,
              kind: 'reference',
            });
          } catch {
            failed++;
          }
        }
        setPendingRefFiles([]);
        setSavingRefs(false);
        if (failed > 0) {
          toast.error(`参考ファイル ${failed} 件の添付に失敗しました（投稿は保存済みです）`);
        }
      }

      toast.success(
        willSchedule
          ? `${formatPublishAt(publishAt)} に公開予約しました`
          : isEdit
            ? '投稿を更新しました'
            : '投稿を作成しました',
      );
      navigate(paths.boardPostDetail.replace(':postId', targetPostId));
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isEdit && existing.isLoading) {
    return (
      <Screen title="投稿を編集" backTo={paths.board}>
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      </Screen>
    );
  }

  // 編集対象が存在しない (削除済み・不正な postId)。空フォームで新規作成に化けるのを防ぐ。
  if (isEdit && !existing.isLoading && !existing.data) {
    return (
      <Screen title="投稿を編集" backTo={paths.board}>
        <EmptyState
          icon={FileQuestion}
          title="投稿が見つかりません"
          description="この投稿は削除されたか、URL が正しくない可能性があります。"
        />
      </Screen>
    );
  }

  const segClass = (active: boolean) =>
    'px-3 py-1.5 rounded-sm text-xs border transition-colors flex items-center gap-1.5 ' +
    (active
      ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
      : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]');

  return (
    <Screen
      title={isEdit ? '投稿を編集' : '新規投稿'}
      description={isEdit ? undefined : '掲示板に新しい投稿を作成します'}
      backTo={
        isEdit && routePostId ? paths.boardPostDetail.replace(':postId', routePostId) : paths.board
      }
      actions={
        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" onClick={() => setTemplateOpen(true)}>
            <LayoutTemplate className="size-4 mr-1" />
            テンプレート
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleSave()}
            disabled={upsert.isPending || savingRefs}
          >
            <Save className="size-4 mr-1" />
            {upsert.isPending ? '保存中...' : willSchedule ? '予約して保存' : '保存'}
          </Button>
        </div>
      }
    >
      <div className="max-w-3xl space-y-5">
        {/* 投稿タイプ */}
        <div>
          <Label>投稿タイプ</Label>
          <div className="flex items-center gap-2">
            {(['decision', 'task', 'info'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={segClass(type === t)}
              >
                {BOARD_POST_TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>

        {/* 件名 */}
        <div>
          <Label>件名 *</Label>
          <div className="flex items-center gap-2">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="件名を入力"
              className="flex-1"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleAssistSubject()}
              disabled={assistSubject.isPending}
              className="shrink-0"
            >
              <Sparkles className="size-4 mr-1" />
              {assistSubject.isPending ? '生成中...' : 'AIアシスト'}
            </Button>
          </div>
        </div>

        {/* カテゴリー */}
        <div>
          <Label>カテゴリー</Label>
          <Select value={categoryId ?? CATEGORY_NONE} onValueChange={handleCategoryChange}>
            <SelectTrigger className="max-w-sm">
              <SelectValue placeholder="カテゴリーを選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CATEGORY_NONE}>未分類</SelectItem>
              {(categories.data ?? []).map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
              <SelectItem value={CATEGORY_ADD_NEW}>＋ 新しいカテゴリーを追加</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* 緊急度 */}
        <div>
          <Label>緊急度</Label>
          <div className="flex items-center gap-2">
            {(['high', 'medium', 'low'] as const).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => setUrgency(u)}
                className={segClass(urgency === u)}
              >
                <span
                  className="inline-block size-2 rounded-full"
                  style={{
                    backgroundColor: BOARD_URGENCY_BAR_COLOR[u] ?? 'var(--text-muted)',
                  }}
                />
                {BOARD_URGENCY_LABEL[u]}
              </button>
            ))}
          </div>
        </div>

        {/* 期日 */}
        <div>
          <Label>期日</Label>
          <div className="flex items-center gap-3">
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={noDueDate}
              className="max-w-[180px]"
            />
            <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer">
              <input
                type="checkbox"
                checked={noDueDate}
                onChange={(e) => setNoDueDate(e.target.checked)}
              />
              無期日
            </label>
          </div>
        </div>

        {/* 宛先 (未選択=全員宛。編集時の差分マージはサーバ側 §2-2-3) */}
        <div>
          <Label>宛先</Label>
          <RecipientPicker value={recipientUids} onChange={setRecipientUids} />
        </div>

        {/*
          閲覧制限 (2026-09-07)。ON にすると宛先と投稿者本人だけが一覧・詳細で閲覧できる。
          宛先が「全員」のときは制限の対象者がいないため効果を持たない旨を表示する。
        */}
        <div>
          <Label>閲覧制限</Label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRestricted(false)}
              className={segClass(!restricted)}
            >
              OFF
            </button>
            <button
              type="button"
              onClick={() => setRestricted(true)}
              className={segClass(restricted)}
            >
              <Lock className="size-3" />
              ON
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
            {!restricted
              ? '社内メンバー全員の投稿一覧に表示されます。'
              : recipientUids.length > 0
                ? '宛先に指定した人と投稿者本人の投稿一覧にだけ表示されます。それ以外の人には表示されません。'
                : '宛先が「全員」のため、閲覧制限は OFF として保存されます。制限するには宛先を指定してください。'}
          </p>
        </div>

        {/*
          公開日時 (投稿予約)。「誰に (宛先) → いつ (公開日時) → 何を (本文)」の
          並びになるよう宛先の直後に置く。期日 (宛先が対応する締切) とは別物なので
          セクションを分けている。
          公開済みの投稿は予約に戻せないためフィールド自体を出さない。
        */}
        {canSchedule && (
          <div>
            <Label>公開日時</Label>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)] pointer-events-none" />
                <Input
                  type="datetime-local"
                  value={publishAt}
                  onChange={(e) => setPublishAt(e.target.value)}
                  min={minPublishAt}
                  disabled={publishNow}
                  className="pl-9 max-w-[240px]"
                />
              </div>
              <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={publishNow}
                  onChange={(e) => setPublishNow(e.target.checked)}
                />
                今すぐ公開
              </label>
            </div>
            <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
              {publishNow || !publishAt ? (
                '保存すると同時に公開され、通知が送信されます。'
              ) : (
                <>
                  <span className="text-[var(--accent)] font-medium">
                    {formatPublishAt(publishAt)}
                  </span>
                  {' に公開されます。公開までは投稿者だけが閲覧でき、通知も公開時に送信されます。'}
                </>
              )}
            </p>
          </div>
        )}

        {/* 本文 */}
        <div>
          <div className="flex items-center justify-between gap-2">
            <Label>本文</Label>
            <Button type="button" variant="secondary" onClick={() => setComposeOpen(true)}>
              <Sparkles className="size-4 mr-1" />
              AI作文
            </Button>
          </div>
          <BoardRichTextEditor
            value={bodyHtml}
            onChange={setBodyHtml}
            postId={targetPostId}
            placeholder="本文を入力..."
          />
        </div>

        {/* 参考ファイル (作業時に参照してほしい資料)。宛先者は詳細画面でダウンロードできる。 */}
        <BoardReferenceAttachments
          postId={targetPostId}
          myUid={user?.uid ?? null}
          postExists={isEdit}
          pendingFiles={pendingRefFiles}
          onPendingChange={setPendingRefFiles}
        />
      </div>

      {/* AI 作文 ダイアログ */}
      <AiComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        currentBodyText={htmlToPlainText(bodyHtml)}
        onApply={(text) => {
          // 既存本文（画像・動画を含む可能性）を丸ごと置換するため確認する
          if (
            htmlToPlainText(bodyHtml) !== '' &&
            !window.confirm('現在の本文を生成結果で置き換えますか？（画像や書式は失われます）')
          ) {
            return;
          }
          // 生成結果はプレーンテキスト。改行を段落に変換して本文へ入れる。
          const html = text
            .split(/\n/)
            .map((line) => `<p>${escapeHtml(line)}</p>`)
            .join('');
          setBodyHtml(html);
        }}
      />

      {/* AI 件名アシスト ダイアログ */}
      <SubjectAssistDialog
        open={assistOpen}
        onOpenChange={setAssistOpen}
        suggestions={assistSuggestions}
        loading={assistSubject.isPending}
        onSelect={(text) => setTitle(text)}
        hasExistingTitle={assistHasTitle}
      />

      {/* テンプレート選択ダイアログ */}
      <TemplatePickerDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        onApply={handleApplyTemplate}
        currentSubject={title}
        currentBodyHtml={bodyHtml}
        currentType={type}
        currentUrgency={urgency}
      />

      {/* カテゴリー新規追加ダイアログ */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>新しいカテゴリーを追加</DialogTitle>
          </DialogHeader>
          <Input
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            placeholder="カテゴリー名"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void handleCreateCategory();
              }
            }}
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setCatDialogOpen(false)}>
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleCreateCategory()}
              disabled={!newCatName.trim() || createCategory.isPending}
            >
              追加
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Screen>
  );
}
